<template>
  <div
    class="grid-layout-wrapper"
    :class="[
      { 'is-preview': isPreview },
      `screen-${String(currentScreen || 'desktop')}`,
    ]"
  >
    <div class="form-grid-row" v-for="(row, rIdx) in tab.rows" :key="rIdx">
      <div
        v-if="!shouldCollapseRow(row)"
        class="grid-col-wrapper"
        :class="[dragHighlightClass(row, rIdx)]"
        @dragover.prevent="onDragOver($event, rIdx, 0, null, false)"
        @drop="onDrop($event, rIdx, 0)"
      >
        <div
          class="form-grid-column"
          v-for="(col, cIdx) in row.cols"
          :key="`${rIdx}-${cIdx}`"
          :style="
            row.cols.length > 1
              ? {
                  width: `calc(${getColumnWidth(rIdx, cIdx)}% - ${
                    ((row.cols.length - 1) * 10) / row.cols.length
                  }px)`,
                }
              : { width: '100%' }
          "
          @dragover.prevent.stop="
            onDragOver($event, rIdx, cIdx, col?.elementId, false)
          "
          @drop="onDropGridPlaceholder($event, rIdx, cIdx)"
        >
          <div
            class="w-100 d-flex align-items-center"
            :class="[getGridColumnDragHightlightClass(rIdx, cIdx)]"
            v-if="!isColsHaveElements(col)"
          >
            <div
              class="placeholder-wrapper"
              v-if="!isPreview"
              @dragover.prevent.stop="
                onDragOver($event, rIdx, cIdx, col?.elementId, true)
              "
            >
              <div
                class="plus-icon-button-wrapper"
                @click.stop="() => toggleAddElementDropDown(rIdx, cIdx)"
              >
                <img :src="require('@/assets/img/form-grid-plus-icon.svg')" />
              </div>
              <div
                class="remove-col-icon-wrapper"
                @click="onRemoveGridCol(rIdx, cIdx)"
              >
                <b-icon icon="x-lg"></b-icon>
              </div>
            </div>
          </div>

          <template v-else>
            <FormBuilderElement
              v-if="getFormElement(col.elementId)"
              :element="getFormElement(col.elementId)"
              :elements="elements"
              :key="col.elementId"
              :tabStructure="tabStructure"
              :tabData="tabData"
              :isPreview="isPreview"
              :responseData="responseData"
              :isActivePropsElement="isActivePropsElement"
              :draggable="draggable"
              :dragItemId="dragItemId"
              :brandedStyle="brandedStyle"
              :rowIndex="rIdx"
              :colIndex="cIdx"
              :tabIndex="tabIndex"
              :isMultipleElementInCol="row.cols.length > 1"
              :dragPosition="getDragPostion(rIdx, cIdx)"
              :disabled="disabled"
              :isDragInsideGrid="isDragInsideGrid"
              :toggleListState="toggleListState"
              :isAskAiModalVisible="isAskAiModalVisible"
              :singleInstanceElementTypes="singleInstanceElementTypes"
              :layoutSetting="layoutSetting"
              :formSettings="formSettings"
              :brandedSubmitBtnStyle="brandedSubmitBtnStyle"
              @removeElement="removeElement"
              @duplicateElement="duplicateElement"
              @insertNewElement="
                (elementId, position) =>
                  insertNewElement(elementId, position, rIdx)
              "
              @editElement="onElementEdit"
              @dragstart="onDragStart($event, col.elementId)"
              @dragover="
                (event) => onDragOver(event, rIdx, cIdx, col.elementId)
              "
              @dragend="onDragEnd"
              @drop="onDrop($event, rIdx, cIdx)"
              @setDragItemId="(id) => $emit('setDragItemId', id)"
              @onToggleListStateChange="onToggleListStateChange"
              @tabButtonSubmit="(event) => $emit('tabButtonSubmit', event)"
            />
          </template>

          <div
            v-if="cIdx != row.cols?.length - 1 && !isPreview"
            @mousedown="startResize($event, rIdx, cIdx)"
            class="resize-handler"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  isColsHaveElements,
  isRowHaveColumnsWithElement,
} from '@/utils/helpers/FormBuilderHelper.js';

// components
import FormBuilderElement from '@/components/FormBuilder/FormBuilderElement.vue';

export default {
  name: 'FormGridLayout',

  data() {
    return {
      isAddElementListVisible: false,
      addElementRowIndex: null,
      addElementColIndex: null,
      // resize
      isResizing: false,
      resizeData: null,
      toggleListState: {},
    };
  },
  props: {
    tab: {
      type: Object,
      required: true,
      default: () => {},
    },
    tabIndex: {
      type: Number,
      required: true,
    },
    elementsToHide: {
      type: Array,
      default: () => [],
    },
    isActivePropsElement: {
      type: Function,
      default: () => false,
    },
    isUserHasAccess: {
      type: Boolean,
      default: false,
    },
    dragPosition: {
      type: String,
      default: null,
    },
    dragOverIndex: {
      type: Object,
      default: () => ({ row: null, col: null }),
    },
    dragItemId: {
      type: Number,
      default: null,
    },
    elements: {
      type: Array,
      required: true,
    },
    responseData: {
      type: Object,
      default: () => ({
        responsePayload: null,
        responseType: null,
        isLoading: false,
      }),
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    isPreview: { type: Boolean, default: false },
    brandedStyle: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
    tabStructure: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    tabData: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    isDragInsideGrid: {
      type: Boolean,
      default: () => false,
    },
    isAskAiModalVisible: {
      type: Boolean,
      default: () => false,
    },
    singleInstanceElementTypes: {
      type: Array,
      default: () => [],
    },
    layoutSetting: {
      type: Object,
      default: () => ({}),
    },
    formSettings: {
      type: Object,
      default: () => ({}),
    },
    currentScreen: {
      type: String,
      default: () => 'desktop',
    },
    brandedSubmitBtnStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  created() {
    this.normalizeWidths();
  },
  watch: {
    tab: {
      handler() {
        this.normalizeWidths();
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.stopResize();
  },

  methods: {
    isColsHaveElements,
    isRowHaveColumnsWithElement,
    toggleAddElementDropDown(rowIndex, colIndex) {
      this.$emit('showAddElementList', rowIndex, colIndex);
    },

    shouldCollapseRow(row) {
      if (!this.isPreview || !row?.cols?.length) return false;
      return row.cols.every((col) => {
        if (!col?.elementId) return false;
        return false;
        // const element = this.getFormElement(col.elementId);
        // return this.isElementHiddenInPreview(element);
      });
    },
    isElementHiddenInPreview(element) {
      if (!this.isPreview || !element) return false;
      return (
        element.type === 'hidden' ||
        (element.settings && element.settings.hidden === true)
      );
    },
    getFormElement(elementId) {
      return this.elements.find((element) => element?.id === elementId);
    },
    onRemoveGridCol(rowIndex, colIndex) {
      this.$emit('removeGridCol', rowIndex, colIndex);
    },
    removeElement(element) {
      this.$emit('removeElement', element);
    },
    duplicateElement(element) {
      this.$emit('duplicateElement', element);
    },
    insertNewElement(elementId, position, rowIndex) {
      this.$emit('insertNewElement', elementId, position, rowIndex);
    },
    onElementEdit(element) {
      this.$emit('editElement', element);
    },
    getColumnWidth(rowIndex, colIndex) {
      const rawWidth = this.tab?.['rows'][rowIndex]?.['cols'][colIndex]?.width;
      if (typeof rawWidth === 'number' && Number.isFinite(rawWidth)) {
        return rawWidth;
      }
      if (typeof rawWidth === 'string') {
        const parsedWidth = parseFloat(rawWidth);
        if (Number.isFinite(parsedWidth)) return parsedWidth;
      }
      return 0;
    },
    normalizeWidths() {
      if (this.isPreview) return;
      this.tab.rows.forEach((row) => {
        let sumSet = 0;
        const unsetIndices = [];
        row.cols.forEach((col, cIdx) => {
          const width = col.width;
          if (Number.isFinite(width) && width > 0) {
            sumSet += width;
          } else {
            unsetIndices.push(cIdx);
          }
        });

        if (unsetIndices.length > 0) {
          let remaining = 100 - sumSet;
          if (remaining <= 0) {
            const minWidth = 10;
            unsetIndices.forEach((i) => (row.cols[i].width = minWidth));
            remaining = 100 - unsetIndices.length * minWidth;
            const setFactor = remaining / sumSet;
            row.cols.forEach((col, cIdx) => {
              if (!unsetIndices.includes(cIdx)) {
                col.width *= setFactor;
              }
            });
          } else {
            const each = remaining / unsetIndices.length;
            unsetIndices.forEach((i) => (row.cols[i].width = each));
          }
        } else if (sumSet !== 100) {
          const factor = 100 / sumSet;
          row.cols.forEach((col) => (col.width *= factor));
        }

        // Skip setting width attribute if there's only one column (100% width)
        if (row.cols.length === 1) {
          delete row.cols[0].width;
        }
      });
    },
    startResize(event, rowIndex, colIndex) {
      if (this.isPreview) return;
      const rowElement = event.target.closest('.form-grid-row');
      const container = event.target.closest('.grid-col-wrapper');
      this.isResizing = true;
      this.resizeData = {
        tabIndex: this.tabIndex,
        rowIndex,
        leftColIndex: colIndex,
        rightColIndex: colIndex + 1,
        startX: event.clientX,
        startLeftWidth: this.getColumnWidth(rowIndex, colIndex),
        startRightWidth: this.getColumnWidth(rowIndex, colIndex + 1),
        container,
        rowElement,
      };

      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
      document.documentElement.classList.add('resizing');
      document.querySelectorAll('.form-grid-column > *').forEach((el) => {
        el.style.pointerEvents = 'none';
      });
      event.preventDefault();
    },
    handleResize(event) {
      if (!this.isResizing || !this.resizeData) return;

      const {
        tabIndex,
        rowIndex,
        leftColIndex,
        rightColIndex,
        startX,
        startLeftWidth,
        startRightWidth,
        container,
      } = this.resizeData;

      if (!container) return;

      const deltaX = event.clientX - startX;
      const containerWidth = container.offsetWidth;
      const numCols = this.tab.rows[rowIndex].cols.length;
      const totalGapWidth = (numCols - 1) * 10;
      const effectiveContainerWidth = containerWidth - totalGapWidth;
      const deltaPercent = (deltaX / effectiveContainerWidth) * 100;

      let newLeftWidth = startLeftWidth + deltaPercent;
      let newRightWidth = startRightWidth - deltaPercent;

      const minWidth = 10;
      if (newLeftWidth < minWidth) {
        newLeftWidth = minWidth;
        newRightWidth = startLeftWidth + startRightWidth - minWidth;
      }
      if (newRightWidth < minWidth) {
        newRightWidth = minWidth;
        newLeftWidth = startLeftWidth + startRightWidth - minWidth;
      }

      const row = this.tabStructure.tabs[tabIndex].rows[rowIndex];
      row.cols[leftColIndex].width = newLeftWidth;
      row.cols[rightColIndex].width = newRightWidth;

      document.documentElement.classList.add('resizing');
    },
    stopResize() {
      this.isResizing = false;
      this.resizeData = null;
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.documentElement.classList.remove('resizing');
      document.querySelectorAll('.form-grid-column > *').forEach((el) => {
        el.style.pointerEvents = '';
      });
    },
    onDragStart(event, elementId) {
      const element = this.getFormElement(elementId);
      this.$emit('dragstart', event, element);
    },
    onDragOver(event, rowIndex, colIndex, elementId, isGridElement = false) {
      const element = elementId ? this.getFormElement(elementId) : '';
      this.$emit('dragover', event, rowIndex, colIndex, element, isGridElement);
    },
    onDragEnd() {
      this.$emit('dragend');
    },
    onDrop(event, rowIndex, colIndex) {
      this.$emit('drop', event, rowIndex, colIndex);
    },
    onDropGridPlaceholder(event, rowIndex, colIndex) {
      this.$emit('drop', event, rowIndex, colIndex);
    },
    getGridColumnDragHightlightClass(rowIndex, colIndex) {
      if (
        rowIndex === this.dragOverIndex.row &&
        colIndex === this.dragOverIndex.col &&
        this.isDragInsideGrid &&
        this.dragPosition
      ) {
        return 'grid-col-dropzone';
      } else {
        return '';
      }
    },
    dragHighlightClass(row, rowIndex) {
      if (row.cols.length <= 1) {
        return '';
      }

      if (rowIndex !== this.dragOverIndex.row || this.isDragInsideGrid)
        return '';

      if (this.dragPosition == 'top') return 'grid-dropzone-above';
      if (this.dragPosition === 'bottom') return 'grid-dropzone-below';
      return '';
    },
    getDragPostion(rowIndex, colIndex) {
      return this.dragOverIndex.col === colIndex &&
        this.dragOverIndex.row === rowIndex
        ? this.dragPosition
        : null;
    },
    onToggleListStateChange(elementId, isOpen) {
      this.$set(this.toggleListState, elementId, isOpen);
    },
  },

  components: {
    FormBuilderElement,
  },
};
</script>

<style scoped>
.grid-layout-wrapper {
  display: flex;
  flex-direction: column;
  /* gap: 10px; */
  position: relative;
  height: 100%;
}
.grid-layout-wrapper .form-grid-row {
  display: flex;
  flex-direction: row;
  width: 100%;
  /* overflow-x: hidden; */
}
.grid-layout-wrapper .grid-col-wrapper {
  position: relative;
  display: flex;
  /* align-items: center; */
  width: 100%;
  gap: 10px;
  box-sizing: border-box;
}
.grid-layout-wrapper.is-preview .grid-col-wrapper {
  min-height: unset;
}
.grid-layout-wrapper .form-grid-column {
  height: 100%;
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  box-sizing: border-box;
  height: fit-content;
}

.grid-layout-wrapper .placeholder-wrapper {
  width: 100%;
  height: calc(100% - 80px);
  /* height: 100%; */
  display: flex;
  opacity: 0;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: 0.3s ease-in-out;
  margin: 40px 0;
  /* border: 1px solid red; */
}

.grid-layout-wrapper .form-grid-column:hover .placeholder-wrapper {
  opacity: 1;
}

.grid-layout-wrapper .plus-icon-button-wrapper {
  cursor: pointer;
}

.grid-layout-wrapper .placeholder-wrapper:hover .plus-icon-button-wrapper {
  display: block;
}

.grid-layout-wrapper .remove-col-icon-wrapper {
  position: absolute;
  top: -25px;
  right: 25px;
  cursor: pointer;
}

.grid-layout-wrapper .resize-handler {
  opacity: 0;
  position: absolute;
  height: 100px;
  width: 15px;
  top: 50%;
  right: -14px;
  transform: translateY(-50%);
  z-index: 51;
  cursor: ew-resize;
  transition: opacity 0.2s ease;
}

.grid-layout-wrapper .resize-handler::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  width: 3px;
  height: 100%;
  background-color: #bebebe;
  transform: translateX(-50%);
  border-radius: 10px;
}

.grid-layout-wrapper .resize-handler:hover {
  opacity: 1;
}

html.resizing,
html.resizing * {
  cursor: ew-resize !important;
}

/* dropzones */

.grid-dropzone-above:has(.grid-col-dropzone) + .grid-dropzone-above::before {
  background-color: transparent;
}
.grid-dropzone-above::before,
.grid-dropzone-below::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 4px;
  z-index: 1;
  background-color: #86c3f4;
}

.grid-col-dropzone::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 4px;
  z-index: 1;
  background-color: #86c3f4;
  top: 0px;
}
.grid-dropzone-above::before {
  top: -2px;
}

.grid-dropzone-below::after {
  bottom: -2px;
}

.grid-layout-wrapper .form-grid-row:first-child .grid-dropzone-above::before {
  top: 0px;
}

.grid-layout-wrapper .form-grid-row:last-child .grid-dropzone-below::after {
  bottom: 0px;
}

.grid-layout-wrapper .form-grid-column:has(.placeholder-wrapper) {
  height: 100%;
}

@media (max-width: 768px) {
  .grid-layout-wrapper .grid-col-wrapper {
    flex-direction: column;
    height: 100%;
  }

  .grid-layout-wrapper .grid-col-wrapper .form-grid-column {
    width: 100% !important ;
  }
}

.grid-layout-wrapper.screen-mobile .grid-col-wrapper,
.grid-layout-wrapper.screen-tablet .grid-col-wrapper {
  flex-direction: column;
  height: 100%;
}

.grid-layout-wrapper.screen-mobile .grid-col-wrapper .form-grid-column,
.grid-layout-wrapper.screen-tablet .grid-col-wrapper .form-grid-column {
  width: 100% !important;
}
</style>
